import { useState, useEffect, useCallback, Fragment } from 'react'
import adminAxios from '../adminAxios'
import { API } from '../api'

interface Registro {
  id: number
  criadoEm: string
  usuarioEmail: string | null
  papel: string | null
  acao: string
  turmaId: number | null
  entidadeId: string | null
  detalhes: Record<string, unknown> | string | null
  status: number | null
  sucesso: number
  ip: string | null
}

interface Props {
  turmas: { ID: number; nome: string }[]
  onError: (err: unknown) => void
}

// Código gravado no banco → texto exibido
export const ACAO_LABELS: Record<string, string> = {
  LOGIN_PAINEL: 'Login no painel',
  LOGIN_PAINEL_FALHA: 'Login no painel recusado',
  ACESSO_NEGADO: 'Acesso negado',
  TURMA_CRIAR: 'Turma criada',
  TURMA_EDITAR: 'Turma editada',
  TURMA_EXCLUIR: 'Turma excluída',
  UNIDADE_CRIAR: 'Unidade criada',
  UNIDADE_EDITAR: 'Unidade editada',
  UNIDADE_EXCLUIR: 'Unidade excluída',
  CARACTERISTICA_CRIAR: 'Característica criada',
  CARACTERISTICA_EDITAR: 'Característica editada',
  CARACTERISTICA_EXCLUIR: 'Característica excluída',
  MANIFESTACAO_CRIAR: 'Manifestação criada',
  MANIFESTACAO_EDITAR: 'Manifestação editada',
  MANIFESTACAO_EXCLUIR: 'Manifestação excluída',
  PARTICIPANTE_CRIAR: 'Participante criado',
  PARTICIPANTE_EDITAR: 'Participante editado',
  PARTICIPANTE_EXCLUIR: 'Participante excluído',
  PARTICIPANTES_CONSULTAR: 'Lista de participantes consultada',
  PARTICIPANTES_IMPORTAR: 'Participantes importados',
  PARAMETRO_CRIAR: 'Parâmetro de filtro criado',
  PARAMETRO_EDITAR: 'Parâmetro de filtro editado',
  PARAMETRO_EXCLUIR: 'Parâmetro de filtro excluído',
  DEBRIEFING_GERAR: 'Debriefing gerado',
  DEBRIEFING_BAIXAR: 'Debriefing baixado',
  PARTICIPANTE_LOGIN: 'Participante entrou',
  PARTICIPANTE_ACEITE_TERMOS: 'Participante aceitou os termos',
  PARTICIPANTE_AUTOCADASTRO: 'Participante se cadastrou (domínio)',
  PARTICIPANTE_SUBMETER: 'Participante submeteu o assessment',
}

const PAPEL_LABELS: Record<string, string> = { admin: 'Admin', facilitador: 'Facilitador', participante: 'Participante' }

const hoje = () => new Date().toLocaleDateString('sv-SE') // AAAA-MM-DD no fuso local
const diasAtras = (n: number) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toLocaleDateString('sv-SE') }

function resumoDetalhes(d: Registro['detalhes']): string {
  if (!d) return ''
  const obj = typeof d === 'string' ? (() => { try { return JSON.parse(d) } catch { return { valor: d } } })() : d
  return Object.entries(obj)
    .filter(([, v]) => v !== '' && v !== null && v !== undefined)
    .map(([k, v]) => `${k}: ${Array.isArray(v) ? `[${v.length}]` : typeof v === 'object' ? '{…}' : String(v)}`)
    .join(' · ')
}

export function AuditoriaAdmin({ turmas, onError }: Props) {
  const [filtros, setFiltros] = useState({ de: diasAtras(7), ate: hoje(), usuario: '', acao: '', turmaId: '' })
  const [itens, setItens] = useState<Registro[]>([])
  const [temMais, setTemMais] = useState(false)
  const [carregando, setCarregando] = useState(false)
  const [aberto, setAberto] = useState<number | null>(null)

  const buscar = useCallback(async (antesDe?: number) => {
    setCarregando(true)
    try {
      const params: Record<string, string> = {}
      // Datas escolhidas no fuso do navegador, enviadas como instante exato
      if (filtros.de) params.de = new Date(`${filtros.de}T00:00:00`).toISOString()
      if (filtros.ate) { const f = new Date(`${filtros.ate}T00:00:00`); f.setDate(f.getDate() + 1); params.ate = f.toISOString() }
      if (filtros.usuario.trim()) params.usuario = filtros.usuario.trim()
      if (filtros.acao) params.acao = filtros.acao
      if (filtros.turmaId) params.turmaId = filtros.turmaId
      if (antesDe) params.antesDe = String(antesDe)
      const res = await adminAxios.get(`${API.ADMIN}/auditoria`, { params })
      setItens(prev => antesDe ? [...prev, ...res.data.itens] : res.data.itens)
      setTemMais(res.data.temMais)
    } catch (err) { onError(err) }
    finally { setCarregando(false) }
  }, [filtros, onError])

  useEffect(() => { buscar() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const nomeTurma = (id: number | null) => {
    if (!id) return ''
    const t = turmas.find(x => x.ID === id)
    return t ? `${id} – ${t.nome.trim()}` : String(id)
  }

  return (
    <div className="aud">
      <h2>Auditoria</h2>
      <p className="aud-sub">Registro das ações do painel e dos eventos dos participantes, do mais recente para o mais antigo.</p>

      <form className="aud-filtros" onSubmit={(e) => { e.preventDefault(); buscar() }}>
        <label>De<input id="aud-de" type="date" value={filtros.de} onChange={(e) => setFiltros({ ...filtros, de: e.target.value })} /></label>
        <label>Até<input id="aud-ate" type="date" value={filtros.ate} onChange={(e) => setFiltros({ ...filtros, ate: e.target.value })} /></label>
        <label>Usuário<input id="aud-usuario" type="text" placeholder="e-mail ou parte dele" value={filtros.usuario} onChange={(e) => setFiltros({ ...filtros, usuario: e.target.value })} /></label>
        <label>Ação
          <select id="aud-acao" value={filtros.acao} onChange={(e) => setFiltros({ ...filtros, acao: e.target.value })}>
            <option value="">Todas</option>
            {Object.entries(ACAO_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label>Turma
          <select id="aud-turma" value={filtros.turmaId} onChange={(e) => setFiltros({ ...filtros, turmaId: e.target.value })}>
            <option value="">Todas</option>
            {turmas.map(t => <option key={t.ID} value={t.ID}>{t.ID} – {t.nome.trim()}</option>)}
          </select>
        </label>
        <button type="submit" disabled={carregando}>{carregando ? 'Buscando…' : 'Filtrar'}</button>
      </form>

      <div className="aud-tabela table-responsive">
        <table>
          <thead>
            <tr><th>Data e hora</th><th>Usuário</th><th>Ação</th><th>Turma</th><th>Detalhes</th><th>Resultado</th></tr>
          </thead>
          <tbody>
            {itens.map(r => (
              <Fragment key={r.id}>
                <tr onClick={() => setAberto(aberto === r.id ? null : r.id)} className={aberto === r.id ? 'aud-sel' : ''}>
                  <td data-rotulo="Data e hora" className="aud-data">{new Date(r.criadoEm).toLocaleString('pt-BR')}</td>
                  <td data-rotulo="Usuário">
                    <span className="aud-email">{r.usuarioEmail || '—'}</span>
                    {r.papel && <span className={`aud-papel aud-papel-${r.papel}`}>{PAPEL_LABELS[r.papel] || r.papel}</span>}
                  </td>
                  <td data-rotulo="Ação">{ACAO_LABELS[r.acao] || r.acao}</td>
                  <td data-rotulo="Turma">{nomeTurma(r.turmaId)}</td>
                  <td data-rotulo="Detalhes" className="aud-det">{r.entidadeId ? `#${r.entidadeId} ` : ''}{resumoDetalhes(r.detalhes)}</td>
                  <td data-rotulo="Resultado"><span className={r.sucesso ? 'aud-ok' : 'aud-erro'}>{r.sucesso ? 'OK' : `Falhou${r.status ? ` (${r.status})` : ''}`}</span></td>
                </tr>
                {aberto === r.id && (
                  <tr className="aud-extra">
                    <td colSpan={6}>
                      <div><b>IP:</b> {r.ip || '—'} · <b>Registro:</b> #{r.id}</div>
                      {r.detalhes && <pre>{JSON.stringify(typeof r.detalhes === 'string' ? JSON.parse(r.detalhes) : r.detalhes, null, 2)}</pre>}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {!itens.length && !carregando && (
              <tr><td colSpan={6} className="aud-vazio">Nenhum registro no período e filtros escolhidos.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {temMais && (
        <button type="button" className="aud-mais" disabled={carregando} onClick={() => buscar(itens[itens.length - 1]?.id)}>
          {carregando ? 'Carregando…' : 'Carregar mais'}
        </button>
      )}
    </div>
  )
}
